import { useEffect, useRef, useState } from "react";
import { API_BASE, readErrorMessage, triggerBrowserDownload } from "./salCommitteeApi";

const POLL_INTERVAL_MS = 5000;
// NeoService2 can be briefly unreachable while its container restarts — the
// gateway answers 502/503/504 then. Keep polling through that instead of
// failing the run on the first blip; give up only after ~3 minutes of
// consecutive gateway errors. After 30 minutes overall stop waiting — the run
// keeps going server-side and emails the result to the requesting user.
const TRANSIENT_STATUSES = [502, 503, 504];
const MAX_TRANSIENT_FAILURES = 36;
const MAX_POLL_MS = 30 * 60 * 1000;
const DEFAULT_FILENAME = "הגשות_לסל.xlsx";

const UPLOADING_MESSAGE = "מעלה את הקובץ...";
const PROCESSING_MESSAGE = "מעבד את קובץ ההגשות ברקע — פעולה זו יכולה לקחת כמה דקות...";
const SERVICE_UNAVAILABLE_MESSAGE = "שירות העיבוד אינו מגיב זמנית — ממשיך לבדוק...";
const STILL_RUNNING_MESSAGE =
  "העיבוד עדיין רץ ברקע. הקובץ יישלח אלייך במייל בסיום, וגם יופיע בהיסטוריית הקבצים.";

const IDLE_STATE = { loading: false, error: null, statusMessage: null, notice: null };

// Status line from run.progress, written by NeoService2 while a Phase A run
// is processing (stage: extracting | companies | excel).
export const progressMessage = (progress) => {
  if (progress?.stage === "extracting") return "מחלץ את הנתונים מקובץ ה-PDF...";
  if (progress?.stage === "companies" && progress.total) {
    return `משלים שמות חברות ממאגר משרד הבריאות — עובדו ${progress.done || 0} מתוך ${progress.total} תרופות`;
  }
  if (progress?.stage === "excel") return "בונה את קובץ ה-Excel...";
  return PROCESSING_MESSAGE;
};

// Rows whose company lookup was cut by NeoService2's time budget.
export const buildSkippedNotice = (run) => {
  const skipped = run.progress?.skipped || 0;
  return skipped
    ? `העיבוד חרג מהזמן המוקצב לחיפוש החברות — ב-${skipped} שורות שם החברה סומן "לא נבדק" בקובץ.`
    : null;
};

const uploadPhaseAFile = async (getAccessToken, file) => {
  const token = await getAccessToken();
  const formData = new FormData();
  formData.append("pdf_file", file);
  const response = await fetch(`${API_BASE}/phase-a/process`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!response.ok) throw new Error(await readErrorMessage(response));
  const { run_id } = await response.json();
  return run_id;
};

// Status of a background run — "processing" | "done" | "failed" (see
// NeoService2's run_history). Null on a transient gateway/network error.
const fetchRunStatus = async (getAccessToken, runId) => {
  const token = await getAccessToken();
  let response;
  try {
    response = await fetch(`${API_BASE}/history/${runId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    return null;
  }
  if (TRANSIENT_STATUSES.includes(response.status)) return null;
  if (!response.ok) throw new Error(`שגיאה בבדיקת סטטוס ההרצה (${response.status})`);
  return response.json();
};

// Polls until the run leaves "processing". Returns { run } when finished,
// { timedOut: true } once MAX_POLL_MS has passed (the run keeps going
// server-side), or { cancelled: true } once isCancelled() (page left).
const waitForRunCompletion = async (getAccessToken, runId, onStatusMessage, isCancelled) => {
  const startedAt = Date.now();
  let transientFailures = 0;
  for (;;) {
    if (isCancelled()) return { cancelled: true };
    const run = await fetchRunStatus(getAccessToken, runId);
    if (run === null) {
      transientFailures += 1;
      if (transientFailures >= MAX_TRANSIENT_FAILURES) {
        throw new Error("שירות העיבוד אינו זמין כבר מספר דקות. יש לבדוק את סטטוס ההרצה בהיסטוריית הקבצים ולהריץ שוב במידת הצורך.");
      }
      onStatusMessage(SERVICE_UNAVAILABLE_MESSAGE);
    } else {
      transientFailures = 0;
      if (run.status !== "processing") return { run };
      onStatusMessage(progressMessage(run.progress));
    }
    if (Date.now() - startedAt > MAX_POLL_MS) return { timedOut: true };
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
  }
};

const downloadRunResult = async (getAccessToken, run, runId) => {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}/history/${runId}/download`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error(`שגיאה בהורדת הקובץ (${response.status})`);
  triggerBrowserDownload(await response.blob(), run.filename || DEFAULT_FILENAME);
};

// Phase A: upload the PDF, poll the background run, download the Excel.
// onFinished runs whenever a run ends or is left running (e.g. reload history).
const usePhaseARun = ({ file, getAccessToken, onFinished }) => {
  const [state, setState] = useState(IDLE_STATE);
  const unmountedRef = useRef(false);
  const setStatusMessage = (statusMessage) => setState((prev) => ({ ...prev, statusMessage }));

  // Stop polling when the page unmounts (the run continues server-side).
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  const runPhaseA = async () => {
    if (!file) {
      setState({ ...IDLE_STATE, error: "יש לבחור קובץ PDF תחילה" });
      return;
    }
    setState({ ...IDLE_STATE, loading: true, statusMessage: UPLOADING_MESSAGE });
    try {
      const runId = await uploadPhaseAFile(getAccessToken, file);
      setStatusMessage(PROCESSING_MESSAGE);
      const { run, timedOut, cancelled } = await waitForRunCompletion(
        getAccessToken,
        runId,
        setStatusMessage,
        () => unmountedRef.current,
      );
      if (cancelled) return;
      if (timedOut) {
        setState({ ...IDLE_STATE, notice: STILL_RUNNING_MESSAGE });
      } else if (run.status === "failed") {
        throw new Error(run.error || "העיבוד נכשל");
      } else {
        await downloadRunResult(getAccessToken, run, runId);
        setState({ ...IDLE_STATE, notice: buildSkippedNotice(run) });
      }
      onFinished();
    } catch (err) {
      setState({ ...IDLE_STATE, error: err.message || "שגיאה בעיבוד קובץ ההגשות" });
    }
  };

  return { phaseAState: state, runPhaseA };
};

export default usePhaseARun;
